import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import CryptoJS from 'crypto-js';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  Home as HomeIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatDate, formatDateTime } from '../../../utils/global';
import { AuditLoggers } from '../../../utils/auditLogger';
import { useUserRights } from '../../../context/UserRightsContext';
import { FC } from '../../../constants/functionCodes';
import {
  matchesWorkflowProductScope,
  workflowProductFunctionCode,
  workflowProductTitle,
  hasWorkflowProductPermission,
} from './workflowProductScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateGradientFormFieldsSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateTabsSx,
} from '../../../theme/harmonateStyles';
import LandingInternalCasRefPage from './LandingInternalCasRefPage';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

const ReferenceFileList = ({
  onBackToWorkflows,
  onBackToReferenceDrawer,
  selectedQuarter,
  selectedReferenceData,
  onFileClick,
  productScope = null,
}) => {
  // State for reference files
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Audit logs state (similar to WorkflowDrawer)
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);

  // New state for additional tabs
  const [activeReferenceTab, setActiveReferenceTab] = useState(0);
  const [workflowInstanceId, setWorkflowInstanceId] = useState(null);
  const workflowInstanceIdRef = useRef(null);
  const [baseInternalCasRefData, setBaseInternalCasRefData] = useState(null);
  const [baseInternalCasRefLoading, setBaseInternalCasRefLoading] = useState(false);
  const [baseInternalCasRefError, setBaseInternalCasRefError] = useState(null);
  const [landingInternalCasRefData, setLandingInternalCasRefData] = useState(null);
  const [landingInternalCasRefLoading, setLandingInternalCasRefLoading] = useState(false);
  const [landingInternalCasRefError, setLandingInternalCasRefError] = useState(null);
  const [uploadedFilesData, setUploadedFilesData] = useState(null);
  const [uploadedFilesLoading, setUploadedFilesLoading] = useState(false);
  const [uploadedFilesError, setUploadedFilesError] = useState(null);

  // Delete functionality state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { hasPermission, isHarmonateUser } = useUserRights();
  const scopedFunctionCode = workflowProductFunctionCode(productScope);
  const canView = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'view')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'view') || hasPermission(FC.ETVL_ENGINE, 'view')));
  const pageTitle = workflowProductTitle(productScope);

  // State for content popup
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  // State for Landing Internal CAS Ref page
  const [showLandingInternalCasRefPage, setShowLandingInternalCasRefPage] = useState(false);
  const [selectedFileForLanding, setSelectedFileForLanding] = useState(null);

  // Fetch reference files using real API
  const fetchReferenceFiles = async (tenantWorkflowId, quarter = selectedQuarter) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      console.log(`Fetching reference files for tenant workflow ID: ${tenantWorkflowId}`);
      console.log(`Using tenant ID: ${tenantId}`);
      console.log(`Using backend URL: ${process.env.REACT_APP_BACKEND_URL}`);

      // Add from/to params for quarter filtering
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-document-type/${tenantWorkflowId}`;
      const params = new URLSearchParams();

      // Add tenantId as query parameter
      params.append('tenantId', tenantId);

      // Add from/to params for quarter filtering
      // Convert date strings to ISO 8601 format with timezone (OffsetDateTime format)
      // Only add dates if quarter is an object (not 'all' string) and has from/to properties
      if (quarter && quarter !== 'all' && typeof quarter === 'object' && quarter.from && quarter.to) {
        // "from" should be start of day: "2026-01-01T00:00:00.000Z"
        // "to" should be end of day: "2026-03-31T23:59:59.999Z"
        const fromDateTime = `${quarter.from}T00:00:00.000Z`;
        const toDateTime = `${quarter.to}T23:59:59.999Z`;
        params.append('from', fromDateTime);
        params.append('to', toDateTime);
      }

      // Append query parameters to URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log(`Making request to: ${url}`);

      // Create headers without tenantId (now passed as query parameter)
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      console.log(`Headers being sent:`, headers);

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`Failed to fetch reference files: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response for reference files:', data);

      // Transform API data - now include the ID for the new API call
      const transformedFiles = data.map((file) => ({
        id: file.id, // This will be used as fileInstanceId for the PDF API (presigned-url)
        name: file.name,
        sourceGeneralLedger: file.sourceGeneralLedger || '', // Map fundName to ID Term
        processStep: file.processStep,
        createdAt: file.createdAt,
        username: file.username || '', // Add username field
        fileInstanceId: file.id, // Use file.id for presigned-url
        fastApiFileInstanceId: file.integrationId, // Use integrationId for FastAPI
        unitOfWorkId: file.unitOfWorkId,
        approvedStatus: file.approvedStatus // <-- Pass approvedStatus
      }));

      // Sort by createdAt descending
      transformedFiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      console.log('Transformed Reference Files:', transformedFiles);
      setReferenceFiles(transformedFiles);

      if (transformedFiles.length === 0) {
        showInfo(`No reference files found for ${selectedReferenceData?.documentType}`, 'Empty Results');
      } else {
        showSuccess(`Successfully loaded ${transformedFiles.length} reference files`, 'Data Loaded');
      }
    } catch (err) {
      console.error('Error fetching reference files:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(err.message);
      setReferenceFiles([]);
      showError(`Failed to load reference files: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  // Load reference files when component mounts or quarter changes
  useEffect(() => {
    if (selectedQuarter && selectedReferenceData && selectedReferenceData.workflowId) {
      fetchReferenceFiles(selectedReferenceData.workflowId, selectedQuarter);
    }
  }, [selectedQuarter, selectedReferenceData]);

  useEffect(() => {
    return () => {
      setIsDeleteMode(false);
      setSelectedRows([]);
      setRowsToDelete([]);
      setDeleteAllConfirmOpen(false);
      setPasswordDialogOpen(false);
      setPassword('');
      setPasswordError('');
    };
  }, [selectedReferenceData]);

  // Handle info click (similar to WorkflowDrawer)
  const handleInfoClick = async (event, row) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${row.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }

      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);

      // Fetch workflow instance ID from unit of work
      let fetchedWorkflowInstanceId = null;
      if (data.document?.unitOfWorkId) {
        fetchedWorkflowInstanceId = await fetchWorkflowInstanceId(data.document.unitOfWorkId);
        console.log('Immediate workflow instance ID:', fetchedWorkflowInstanceId);
      }

      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Delete functionality
  const handleDeleteClick = (event, row) => {
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  // Password verification helpers (match WorkflowDrawer flow)
  const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

  const generateKey = () => {
    const keyBytes = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
    const hash = CryptoJS.SHA256(keyBytes);
    return CryptoJS.lib.WordArray.create(hash.words.slice(0, 8));
  };

  const encryptData = (data) => {
    if (!data) return '';
    try {
      const stringData = data.toString();
      const key = generateKey();
      const encrypted = CryptoJS.AES.encrypt(stringData, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return encrypted.toString();
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Encryption failed: ' + error.message);
    }
  };

  const verifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError('Please enter your password');
      return false;
    }

    try {
      setIsVerifying(true);
      setPasswordError('');

      const username = localStorage.getItem('username');
      if (!username) {
        throw new Error('User session expired. Please log in again.');
      }

      const encryptedPassword = encryptData(password);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}auth/signin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username,
            password: encryptedPassword,
            applicationId: 1,
            application: "Conductor"
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Incorrect password');
      }

      return true;
    } catch (error) {
      console.error('Password verification failed:', error);
      setPasswordError(error.message || 'Failed to verify password');
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRowSelection = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleDeleteAllClick = (e) => {
    e?.preventDefault();

    if (selectedRows.length === 0) {
      if (!isDeleteMode) {
        setIsDeleteMode(true);
      }
      return;
    }

    setRowsToDelete([...selectedRows]);
    setDeleteAllConfirmOpen(true);
  };

  const handleCancelDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedRows([]);
    setRowsToDelete([]);
    setDeleteAllConfirmOpen(false);
  };

  const executeBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get file names for logging
    const fileNames = rowsToDelete.map(id => {
      const file = referenceFiles.find(f => f.id === id);
      return file?.name || file?.fileName || 'Unknown';
    });
    // Log the bulk delete attempt
    await AuditLoggers.bulkFileDeletionInitiated(
      rowsToDelete,
      fileNames,
      'reference',
      { 
        documentTypeId: selectedReferenceData?.documentTypeId,
        workflowId: selectedReferenceData?.workflowId
      }
    );

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/bulk-delete`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(rowsToDelete)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete files');
      }

      const result = await response.json();

      // FastAPI: bulk status sync must succeed before showing success
      const statusUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-v2-status/bulk-save`;
      console.log('Calling bulk status update API at URL:', statusUrl);

      const statusRecords = rowsToDelete.map((id) => {
        const file = referenceFiles.find((f) => f.id === id);
        return {
          file_instance_id: file?.fastApiFileInstanceId?.toString() || id?.toString(),
          workflow_instance_id: file?.processId?.toString() || id?.toString(),
          status: 'DELETED',
        };
      });

      const statusResponse = await fetch(statusUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ records: statusRecords }),
      });

      if (!statusResponse.ok) {
        const statusErrorText = await statusResponse.text();
        console.error('Bulk status update error response text:', statusErrorText);
        throw new Error(`Files deleted in Conductor, but FastAPI bulk status update failed: ${statusResponse.status} - ${statusErrorText}`);
      }

      const statusResult = await statusResponse.json();
      console.log('Bulk status update successful:', statusResult);

      showSuccess(`Successfully deleted ${result.deletedCount} files`);
      setSelectedRows([]);
      setRowsToDelete([]);
      setIsDeleteMode(false);
      handleRetry(); // Refresh the list
    } catch (error) {
      console.error('Error deleting files:', error);
      showError(error.message || 'Failed to delete files');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDelete = () => {
    if (rowsToDelete.length === 0) {
      showError('No files selected for deletion');
      setDeleteAllConfirmOpen(false);
      return;
    }

    // Close confirmation and prompt for password before deleting
    setDeleteAllConfirmOpen(false);
    setPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async () => {
    const isValid = await verifyPassword();
    if (isValid) {
      setPasswordDialogOpen(false);
      setPassword('');

      await executeBulkDelete();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Log file deletion attempt
      try {
        const fileName = fileToDelete.name || fileToDelete.fileName || fileToDelete.originalFileName || 'unknown';
        console.log('Logging file deletion attempt...', { fileToDelete, fileName });
        await AuditLoggers.fileDeletionInitiated(
          fileToDelete.id,
          fileName,
          'reference' // Since this is the reference file list
        );
      } catch (logError) {
        console.error('Error logging file deletion:', logError);
        // Continue with deletion even if logging fails
      }

      const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileToDelete.id}/delete`;
      console.log('Calling delete API at URL:', deleteUrl);

      const response = await fetch(deleteUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete error response text:', errorText);
        throw new Error(`Failed to delete file: ${response.status} - ${errorText}`);
      }

      // FastAPI: status sync must succeed before showing success
      const statusUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-v2-status/save`;
      console.log('Calling status update API at URL:', statusUrl);

      const statusResponse = await fetch(statusUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_instance_id:
            fileToDelete.fastApiFileInstanceId?.toString() || fileToDelete.id?.toString(),
          workflow_instance_id:
            fileToDelete.processId?.toString() || fileToDelete.id?.toString(),
          status: 'DELETED',
        }),
      });

      if (!statusResponse.ok) {
        const statusErrorText = await statusResponse.text();
        console.error('Status update error response text:', statusErrorText);
        throw new Error(`File deleted in Conductor, but FastAPI status update failed: ${statusResponse.status} - ${statusErrorText}`);
      }

      const statusResult = await statusResponse.json();
      console.log('Status update successful:', statusResult);

      showSuccess('File deleted successfully');
      setDeleteConfirmOpen(false);
      setFileToDelete(null);

      // Refresh reference files
      if (selectedQuarter && selectedReferenceData && selectedReferenceData.workflowId) {
        fetchReferenceFiles(selectedReferenceData.workflowId, selectedQuarter);
      }
    } catch (error) {
      console.error('Error during file deletion:', error);
      showError(`Failed to delete file: ${error.message}`);
    }
  };

  // Fetch workflow instance ID from unit of work
  const fetchWorkflowInstanceId = async (unitOfWorkId) => {
    console.log('Fetching workflow instance ID for unitOfWorkId:', unitOfWorkId);
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${unitOfWorkId}`;
      console.log('UOW API URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('UOW API response status:', response.status);

      if (response.ok) {
        const workflowData = await response.json();
        console.log('UOW API full response:', workflowData);

        // The response IS the workflow instance ID directly
        const workflowId = workflowData;
        console.log('Extracted workflow instance ID:', workflowId);
        setWorkflowInstanceId(workflowId);
        workflowInstanceIdRef.current = workflowId;
        console.log('Workflow instance ID set to:', workflowId);

        // Return the ID so it can be used immediately
        return workflowId;
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch workflow instance ID:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching workflow instance ID:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    console.log('Tab changed to:', newValue);
    const currentWorkflowId = workflowInstanceIdRef.current || workflowInstanceId;
    console.log('Available data:', {
      documentDetails: documentDetails?.integrationId,
      workflowInstanceId,
      workflowInstanceIdRef: workflowInstanceIdRef.current,
      currentWorkflowId,
      baseInternalCasRefData: !!baseInternalCasRefData,
      landingInternalCasRefData: !!landingInternalCasRefData
    });

    setActiveReferenceTab(newValue);

    // Fetch data for the selected tab if not already loaded
    if (newValue === 1 && !baseInternalCasRefData && !baseInternalCasRefLoading && documentDetails?.integrationId && currentWorkflowId) {
      console.log('Fetching Base Internal CAS Ref data with workflow ID:', currentWorkflowId);
      fetchBaseInternalCasRefData(currentWorkflowId);
    } else if (newValue === 2 && !landingInternalCasRefData && !landingInternalCasRefLoading && documentDetails?.integrationId && currentWorkflowId) {
      console.log('Fetching Landing Internal CAS Ref data with workflow ID:', currentWorkflowId);
      fetchLandingInternalCasRefData(currentWorkflowId);
    } else if (newValue === 3 && !uploadedFilesData && !uploadedFilesLoading && documentDetails?.integrationId) {
      console.log('Fetching Uploaded Files data...');
      fetchUploadedFilesData();
    } else if ((newValue === 1 || newValue === 2) && !currentWorkflowId) {
      console.log('Cannot fetch data - workflowInstanceId is missing');
    }
  };

  // Add this function before fetchBaseInternalCasRefData
  const fetchWorkflowDefinition = async (documentTypeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/document-type/${documentTypeId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch workflow definition:', response.status, errorText);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching workflow definition:', error);
      return null;
    }
  };

  const fetchBaseInternalCasRefData = async (workflowId = null) => {
    const actualWorkflowId = workflowId || workflowInstanceId;
    const documentTypeId = selectedReferenceData?.id || selectedReferenceData?.documentTypeId;

    if (!documentDetails?.integrationId || !actualWorkflowId || !documentTypeId) {
      console.log('Missing required data for Base Internal CAS Ref:', {
        integrationId: documentDetails?.integrationId,
        workflowInstanceId: actualWorkflowId,
        documentTypeId: documentTypeId,
        selectedReferenceData: selectedReferenceData
      });
      return;
    }

    console.log('Starting Base Internal CAS Ref fetch...');
    setBaseInternalCasRefLoading(true);
    setBaseInternalCasRefError(null);

    try {
      // First, fetch the workflow definition to get the snowflake table name
      const workflowDef = await fetchWorkflowDefinition(documentTypeId);
      // Use the snowflakeTableName from the API response, default to 'INTERNAL_CAS_REF' if not found
      const tableName = workflowDef?.[0]?.snowflakeTableName;
      console.log('Using table name:', tableName);

      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/base-ref?file_id=${documentDetails.integrationId}&workflow_id=${actualWorkflowId}&table_name=${tableName}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        url += `&tenant_id=${tenantId}`;
      }
      console.log('Base Internal CAS Ref URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Base Internal CAS Ref response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Base Internal CAS Ref error response:', errorText);
        throw new Error(`Failed to fetch base internal CAS ref data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Base Internal CAS Ref data received:', data);
      setBaseInternalCasRefData(data);
    } catch (err) {
      console.error('Error fetching base internal CAS ref data:', err);
      setBaseInternalCasRefError(err.message);
    } finally {
      setBaseInternalCasRefLoading(false);
    }
  };



  const fetchLandingInternalCasRefData = async (workflowId = null) => {
    const actualWorkflowId = workflowId || workflowInstanceId;
    const documentTypeId = selectedReferenceData?.id || selectedReferenceData?.documentTypeId;

    if (!documentDetails?.integrationId || !actualWorkflowId || !documentTypeId) {
      console.log('Missing required data for Landing Internal CAS Ref:', {
        integrationId: documentDetails?.integrationId,
        workflowInstanceId: actualWorkflowId,
        documentTypeId: documentTypeId
      });
      return;
    }

    console.log('Starting Landing Internal CAS Ref fetch...');
    setLandingInternalCasRefLoading(true);
    setLandingInternalCasRefError(null);

    try {
      // First, fetch the workflow definition to get the snowflake table name
      const workflowDef = await fetchWorkflowDefinition(documentTypeId);
      // Use the snowflakeTableName from the API response, default to 'EXTERNAL_FS_SOI_REF' if not found
      const tableName = workflowDef?.[0]?.snowflakeTableName;
      console.log('Using landing table name:', tableName);

      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/landing-ref?file_id=${documentDetails.integrationId}&workflow_id=${actualWorkflowId}&table_name=${tableName}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        url += `&tenant_id=${tenantId}`;
      }
      console.log('Landing Internal CAS Ref URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Landing Internal CAS Ref response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Landing Internal CAS Ref error response:', errorText);
        throw new Error(`Failed to fetch landing internal CAS ref data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Landing Internal CAS Ref data received:', data);
      setLandingInternalCasRefData(data);
    } catch (err) {
      console.error('Error fetching landing internal CAS ref data:', err);
      setLandingInternalCasRefError(err.message);
    } finally {
      setLandingInternalCasRefLoading(false);
    }
  };

  const fetchUploadedFilesData = async () => {
    if (!documentDetails?.integrationId) {
      console.log('Missing required data for Uploaded Files:', {
        integrationId: documentDetails?.integrationId
      });
      return;
    }

    console.log('Starting Uploaded Files fetch...');
    setUploadedFilesLoading(true);
    setUploadedFilesError(null);

    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/uploaded-files?reference_data_id=${documentDetails.integrationId}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        url += `&tenant_id=${tenantId}`;
      }
      console.log('Uploaded Files URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Uploaded Files response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Uploaded Files error response:', errorText);
        throw new Error(`Failed to fetch uploaded files data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Uploaded Files data received:', data);
      setUploadedFilesData(data);
    } catch (err) {
      console.error('Error fetching uploaded files data:', err);
      setUploadedFilesError(err.message);
    } finally {
      setUploadedFilesLoading(false);
    }
  };

  const handleFileClick = async (params) => {
    if (!canView) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    if (!matchesWorkflowProductScope(selectedReferenceData?.documentType, productScope)) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    // Open Landing Internal CAS Ref page on row click
    await handleOpenLandingInternalCasRef(params.row);
  };

  // Updated JSON renderer function
  const renderJsonData = (data) => {
    if (!data) return <Typography sx={{ color: ht.text }}>No data available</Typography>;

    // If data has 'data' array, render as grid
    if (Array.isArray(data.data) && data.data.length > 0) {
      // Dynamically generate columns from unique keys
      const allKeys = new Set();
      data.data.forEach(item => Object.keys(item).forEach(key => allKeys.add(key)));
      const excludedKeys = ['CONTENT', 'CONTENT_HASH']; // Exclude large/unneeded
      const columns = Array.from(allKeys)
        .filter(key => !excludedKeys.includes(key))
        .map(key => ({
          field: key,
          headerName: key.replace(/([A-Z])/g, ' $1').trim(), // Human-readable header
          flex: 1,
          minWidth: 150,
          sortable: true,
          filterable: true,
          valueFormatter: (params) => {
            if (key.includes('ON') || key.includes('DATE')) {
              return params.value ? new Date(params.value).toLocaleString() : 'N/A';
            }
            return params.value;
          }
        }));

      if (allKeys.has('CONTENT')) {
        columns.push({
          field: 'CONTENT',
          headerName: 'Content',
          flex: 1,
          minWidth: 150,
          sortable: false,
          renderCell: (params) => (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedContent(params.row.CONTENT);
                setIsContentModalOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              View Content
            </Button>
          ),
        });
      }

      // Replace the current return block in renderJsonData with this if removing metadata
      return (
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGridPro
            rows={data.data.map((item, index) => ({ id: index, ...item }))}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            columnResizeMode="onEnd"
            resizable
            disableRowSelectionOnClick
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            sx={{
              ...harmonateDataGridSx,
              border: 'none',
              '& .MuiDataGrid-cell': {
                fontSize: '0.875rem',
                color: ht.text,
              },
            }}
          />
        </Box>
      );
    }

    // Fallback to JSON view for non-tabular data
    return (
      <Box sx={{
        backgroundColor: ht.listBg,
        border: `1px solid ${ht.line}`,
        borderRadius: ht.cardRadius,
        p: 2,
        overflow: 'auto',
        maxHeight: '400px',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        color: ht.text,
      }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Box>
    );
  };

  const handleRetry = () => {
    if (selectedQuarter && selectedReferenceData && selectedReferenceData.workflowId) {
      fetchReferenceFiles(selectedReferenceData.workflowId, selectedQuarter);
    }
  };

  // Handler for opening Landing Internal CAS Ref page
  const handleOpenLandingInternalCasRef = async (file) => {
    try {
      // First, we need to get the workflow instance ID from the unit of work
      let workflowInstanceId = null;

      if (file.unitOfWorkId) {
        const token = localStorage.getItem('token');
        const url = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${file.unitOfWorkId}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          workflowInstanceId = await response.json();
          console.log('Fetched workflow instance ID for Landing Internal CAS Ref:', workflowInstanceId);
        } else {
          console.error('Failed to fetch workflow instance ID:', response.status);
          showError('Failed to get workflow instance ID');
          return;
        }
      }

      setSelectedFileForLanding({
        ...file,
        integrationId: file.fastApiFileInstanceId || file.integrationId,
        workflowId: workflowInstanceId, // Use the actual workflow instance ID (integer)
       documentTypeId: selectedReferenceData?.id || selectedReferenceData?.documentTypeId // Add this line
    });
      setShowLandingInternalCasRefPage(true);
    } catch (error) {
      console.error('Error preparing Landing Internal CAS Ref page:', error);
      showError('Failed to prepare Landing Internal CAS Ref page');
    }
  };

  // Handler for going back from Landing Internal CAS Ref page
  const handleBackFromLandingInternalCasRef = () => {
    setShowLandingInternalCasRefPage(false);
    setSelectedFileForLanding(null);
  };

  // Filter files based on search query
  const filteredFiles = referenceFiles.filter(file => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (file.name && file.name.toLowerCase().includes(searchLower)) ||
      (file.sourceGeneralLedger && file.sourceGeneralLedger.toLowerCase().includes(searchLower)) ||
      (file.processStep && file.processStep.toLowerCase().includes(searchLower))
    );
  });

  // DataGrid columns (added delete button next to info button)
  const fallbackHarmonate = isServerHarmonateUser();
  const effectiveIsHarmonate = typeof isHarmonateUser === 'boolean' ? isHarmonateUser : fallbackHarmonate;
  const canDeleteWorkflow = hasWorkflowProductPermission(
    hasPermission,
    effectiveIsHarmonate,
    productScope,
    'delete'
  );

  const columns = [
    { field: 'name', headerName: 'File Name', flex: 1 },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1,
      width: 200,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.7rem' }}>
          {params.value ? formatDateTime(params.value) : ''}
        </Typography>
      ),
    },
    { field: 'sourceGeneralLedger', headerName: 'Source GL', width: 300 },
    { field: 'username', headerName: 'Uploaded By', width: 150 },
    { field: 'processStep', headerName: 'Process Step', width: 130 },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => (
        <>
          {effectiveIsHarmonate && (
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={(e) => handleInfoClick(e, params.row)}
                sx={{ color: 'primary.main', mr: 1 }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteWorkflow && (
            <Tooltip title="Delete file">
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, params.row)}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  // If showing Landing Internal CAS Ref page, render that instead
  if (showLandingInternalCasRefPage && selectedFileForLanding) {
    return (
      <LandingInternalCasRefPage
        onBackToWorkflows={onBackToWorkflows}
        onBackToReferenceDrawer={onBackToReferenceDrawer}
        selectedQuarter={selectedQuarter}
        selectedReferenceData={selectedReferenceData}
        selectedFile={selectedFileForLanding}
        onBackToReferenceFiles={handleBackFromLandingInternalCasRef}
      />
    );
  }

  return (
    <>
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              {pageTitle}
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToReferenceDrawer}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <FolderOpenIcon sx={{ fontSize: '1rem' }} />
              Reference Files
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DescriptionIcon sx={{ fontSize: '1rem' }} />
              {selectedReferenceData?.documentType || 'File List'}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Search & Delete Controls */}
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={(e) => e.preventDefault()}
          sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}
        >
          <TextField
            label="Search Files"
            variant="outlined"
            size="medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: 350,
              ...harmonateGradientFormFieldsSx,
              '& .MuiInputBase-root': {
                fontSize: '0.9rem',
                height: 48,
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.9rem',
              }
            }}
            placeholder="Search by name, GL, or process step"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: ht.white }} />
                </InputAdornment>
              ),
            }}
          />
          {canDeleteWorkflow && (
            <>
              <Button
                variant={isDeleteMode ? "outlined" : "contained"}
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAllClick}
                disabled={!canDeleteWorkflow}
                sx={{ height: 48, minWidth: 160 }}
                type="button"
              >
                {!isDeleteMode
                  ? 'Bulk Delete'
                  : selectedRows.length > 0
                    ? `Delete Selected (${selectedRows.length})`
                    : 'Select files'}
              </Button>
              {isDeleteMode && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancelDeleteMode}
                  sx={{ height: 48, minWidth: 140 }}
                  type="button"
                >
                  Cancel
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Content */}
        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, overflow: 'hidden' }}>
          {/* Loading state */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress sx={{ color: ht.green }} />
              <Typography variant="body1" sx={{ ml: 2 }}>Loading reference files...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '400px', justifyContent: 'center' }}>
              <Alert severity="error" sx={{ mb: 2, maxWidth: '500px' }}>
                <Typography>Failed to load reference files: {error}</Typography>
              </Alert>
              <Button
                variant="outlined"
                onClick={handleRetry}
                sx={harmonateUploadOutlinedSx}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <DataGridPro
              rows={filteredFiles}
              columns={columns}
              rowHeight={40}
              columnResizeMode="onEnd"
              resizable
              onRowClick={handleFileClick}
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              checkboxSelection={isDeleteMode}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={isDeleteMode ? selectedRows : []}
              disableRowSelectionOnClick
              sx={{
                ...harmonateDataGridSx,
                height: 700,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.7rem',
                },
                '& .MuiDataGrid-columnHeader': {
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer'
                }
              }}
              initialState={{
                filter: {
                  filterModel: {
                    items: [
                      { columnField: 'name', operatorValue: 'contains', value: '' },
                      { columnField: 'date', operatorValue: 'equals', value: null },
                    ],
                  },
                },
              }}
              getRowId={(row) => row.id}
              paginationMode="server"
              onPageChange={(newPage) => console.log(newPage)}
              onPageSizeChange={(newPageSize) => console.log(newPageSize)}
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No files match your search' : 'No reference files found'}
                  </Typography>
                </Box>
              )}
            />
          )}
        </Paper>
      </Box>

      {/* Document Details & Audit Logs Modal (copied from WorkflowDrawer) */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => {
            setIsLogsModalOpen(false);
            setActiveReferenceTab(0);
            setWorkflowInstanceId(null);
            workflowInstanceIdRef.current = null;
            setBaseInternalCasRefData(null);
            setBaseInternalCasRefError(null);
            setLandingInternalCasRefData(null);
            setLandingInternalCasRefError(null);
            setUploadedFilesData(null);
            setUploadedFilesError(null);
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              ...harmonateCardSx,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${ht.line}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: ht.green,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, flexGrow: 1, color: ht.white, fontSize: '1.1rem' }}
              >
                Document Details & Audit Logs
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setIsLogsModalOpen(false);
                  setActiveReferenceTab(0);
                  setWorkflowInstanceId(null);
                  workflowInstanceIdRef.current = null;
                  setBaseInternalCasRefData(null);
                  setBaseInternalCasRefError(null);
                  setLandingInternalCasRefData(null);
                  setLandingInternalCasRefError(null);
                  setUploadedFilesData(null);
                  setUploadedFilesError(null);
                }}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.green }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {/* Document Details Section */}
                {documentDetails && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      '& > div': {
                        minWidth: 0, // Prevents overflow
                      }
                    }}>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Name</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Type</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Uploaded At</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.uploadAt ? new Date(documentDetails.uploadAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Location</Typography>
                        <Tooltip
                          title={documentDetails.location || 'N/A'}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              fontSize: '0.875rem',
                              color: ht.text,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationColor: ht.text,
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '2px',
                              }
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Unit of Work</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.unitOfWorkId || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Integration ID</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Source General Ledger</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.sourceGeneralLedger || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                {/* Tabbed Data Section */}
                <Box sx={{ mt: 1 }}>
                  <Tabs
                    value={activeReferenceTab}
                    onChange={handleTabChange}
                    sx={{
                      mb: 3,
                      ...harmonateTabsSx,
                    }}
                  >
                    <Tab label="Audit Logs" />
                    <Tab label="Base Ref" />
                    <Tab label="Landing Ref" />
                    <Tab label="Uploaded Files" />
                  </Tabs>

                  <Box sx={{
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                    p: 3,
                  }}>
                    {activeReferenceTab === 0 && (
                      <>
                        {/* Audit Logs Section */}
                        <Box>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5,
                            pb: 1,
                            borderBottom: `1px solid ${ht.line}`,
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                              AUDIT LOGS
                            </Typography>
                          </Box>
                          {auditLogs.length > 0 ? (
                            <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                              <DataGridPro
                                rows={auditLogs}
                                columnResizeMode="onEnd"
                                resizable
                                pagination
                                rowsPerPageOptions={[10, 25, 50]}
                                pageSize={10}
                                autoPageSize={false}
                                columns={[
                                  {
                                    field: 'createdAt',
                                    headerName: 'Timestamp',
                                    flex: 1,
                                    minWidth: 160,
                                    valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
                                    renderHeader: () => (
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                        Timestamp
                                      </Typography>
                                    )
                                  },
                                  {
                                    field: 'service',
                                    headerName: 'Service',
                                    flex: 0.8,
                                    minWidth: 150,
                                    cellClassName: 'service-cell',
                                    renderHeader: () => (
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                        Service
                                      </Typography>
                                    ),
                                    renderCell: (params) => (
                                      <Box sx={{
                                        bgcolor: params.value === 'upload' ? ht.secondaryTab :
                                          params.value === 'document_classification' ? 'rgba(2, 107, 29, 0.12)' :
                                            params.value === 'extraction' ? 'rgba(46, 166, 160, 0.18)' :
                                              params.value === 'ingest' ? 'rgba(1, 48, 98, 0.12)' : ht.listBg,
                                        color: params.value === 'upload' ? ht.text :
                                          params.value === 'document_classification' ? ht.green :
                                            params.value === 'extraction' ? ht.blue :
                                              params.value === 'ingest' ? ht.text : ht.text,
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: ht.buttonRadius,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        minWidth: 'fit-content',
                                        width: 'auto',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        {params.value || 'N/A'}
                                      </Box>
                                    )
                                  },
                                  {
                                    field: 'action',
                                    headerName: 'Action',
                                    flex: 1,
                                    minWidth: 120,
                                    renderHeader: () => (
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                        Action
                                      </Typography>
                                    ),
                                    valueFormatter: (params) => params.value || 'N/A',
                                    renderCell: (params) => (
                                      <Box sx={{
                                        color: ht.text,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        fontFamily: 'monospace'
                                      }}>
                                        {params.formattedValue}
                                      </Box>
                                    )
                                  },
                                  {
                                    field: 'status',
                                    headerName: 'Status',
                                    flex: 0.8,
                                    minWidth: 100,
                                    renderHeader: () => (
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                        Status
                                      </Typography>
                                    ),
                                    renderCell: (params) => (
                                      <Box sx={{
                                        color: ht.text,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase'
                                      }}>
                                        {params.value || 'N/A'}
                                      </Box>
                                    )
                                  },
                                  {
                                    field: 'details',
                                    headerName: 'Details',
                                    flex: 2,
                                    minWidth: 200,
                                    renderHeader: () => (
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                        Details
                                      </Typography>
                                    ),
                                    renderCell: (params) => (
                                      <Tooltip
                                        title={params.value ?
                                          (typeof params.value === 'object' ?
                                            JSON.stringify(params.value, null, 2) :
                                            params.value.toString()) :
                                          'N/A'}
                                        arrow
                                        placement="top"
                                      >
                                        <Box sx={{
                                          width: '100%',
                                          '&:hover': {
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            textDecorationColor: ht.text,
                                            textDecorationThickness: '1px',
                                            textUnderlineOffset: '2px',
                                          }
                                        }}>
                                          <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{
                                              color: ht.text,
                                              fontSize: '0.8rem',
                                              fontFamily: 'monospace',
                                            }}
                                          >
                                            {params.value ?
                                              (typeof params.value === 'object' ?
                                                JSON.stringify(params.value) :
                                                params.value.toString()
                                              ).substring(0, 50) + (params.value.length > 50 ? '...' : '') :
                                              'N/A'}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    )
                                  }
                                ]}
                                disableSelectionOnClick
                                disableColumnMenu
                                hideFooterSelectedRowCount
                                sx={{
                                  ...harmonateDataGridSx,
                                  border: 'none',
                                  '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: ht.listBg,
                                    borderBottom: `1px solid ${ht.line}`,
                                    minHeight: '40px !important',
                                    maxHeight: '40px !important',
                                  },
                                  '& .MuiDataGrid-columnHeader': {
                                    py: 1,
                                  },
                                  '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 600,
                                    color: ht.text,
                                  },
                                  '& .MuiDataGrid-cell': {
                                    fontSize: '0.8125rem',
                                    color: ht.text,
                                    py: 1,
                                    borderColor: ht.line,
                                  },
                                  '& .MuiDataGrid-row': {
                                    '&:hover': {
                                      backgroundColor: ht.secondaryTab,
                                    },
                                    '&.Mui-selected': {
                                      backgroundColor: ht.secondaryTab,
                                      '&:hover': {
                                        backgroundColor: ht.secondaryTab,
                                      },
                                    },
                                  },
                                  '& .MuiDataGrid-footerContainer': {
                                    borderTop: `1px solid ${ht.line}`,
                                    minHeight: '48px',
                                  },
                                }}
                                components={{
                                  LoadingOverlay: LinearProgress,
                                  NoRowsOverlay: () => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                      <Typography variant="body2" sx={{ color: ht.text }}>
                                        No audit logs available
                                      </Typography>
                                    </Box>
                                  ),
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography sx={{ color: ht.text }}>No audit logs available</Typography>
                          )}
                        </Box>
                      </>
                    )}

                    {activeReferenceTab === 1 && (
                      <>
                        {baseInternalCasRefLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress sx={{ color: ht.green }} />
                          </Box>
                        ) : baseInternalCasRefError ? (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            <AlertTitle>Error Loading Base Internal CAS Ref Data</AlertTitle>
                            {baseInternalCasRefError}
                          </Alert>
                        ) : baseInternalCasRefData ? (
                          renderJsonData(baseInternalCasRefData)
                        ) : (
                          <Typography sx={{ color: ht.text }}>
                            Click to load Base Internal CAS Ref data
                          </Typography>
                        )}
                      </>
                    )}

                    {activeReferenceTab === 2 && (
                      <>
                        {landingInternalCasRefLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress sx={{ color: ht.green }} />
                          </Box>
                        ) : landingInternalCasRefError ? (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            <AlertTitle>Error Loading Landing Internal CAS Ref Data</AlertTitle>
                            {landingInternalCasRefError}
                          </Alert>
                        ) : landingInternalCasRefData ? (
                          renderJsonData(landingInternalCasRefData)
                        ) : (
                          <Typography sx={{ color: ht.text }}>
                            Click to load Landing Internal CAS Ref data
                          </Typography>
                        )}
                      </>
                    )}

                    {activeReferenceTab === 3 && (
                      <>
                        {uploadedFilesLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress sx={{ color: ht.green }} />
                          </Box>
                        ) : uploadedFilesError ? (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            <AlertTitle>Error Loading Uploaded Files Data</AlertTitle>
                            {uploadedFilesError}
                          </Alert>
                        ) : uploadedFilesData ? (
                          renderJsonData(uploadedFilesData)
                        ) : (
                          <Typography sx={{ color: ht.text }}>
                            Click to load Uploaded Files data
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={deleteAllConfirmOpen}
        onClose={() => !isBulkDeleting && setDeleteAllConfirmOpen(false)}
        aria-labelledby="delete-all-dialog-title"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="delete-all-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete {rowsToDelete.length} selected file(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteAllConfirmOpen(false)}
            disabled={isBulkDeleting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            disabled={isBulkDeleting}
            startIcon={isBulkDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={harmonateAbortButtonSx}
          >
              {isBulkDeleting ? 'Processing...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Confirmation Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => !isVerifying && setPasswordDialogOpen(false)}
        aria-labelledby="password-dialog-title"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="password-dialog-title">Confirm Your Identity</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please enter your password to confirm deletion of {rowsToDelete.length} file(s).
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            autoComplete="off"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError('');
            }}
            error={!!passwordError}
            helperText={passwordError}
            disabled={isVerifying}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordDialogOpen(false);
              setPassword('');
              setPasswordError('');
            }}
            disabled={isVerifying}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordConfirm}
            variant="contained"
            disabled={isVerifying || !password.trim()}
            startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
            sx={harmonateAbortButtonSx}
          >
            {isVerifying ? 'Verifying...' : 'Confirm Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="delete-dialog-title">
          Confirm File Deletion
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this file? This action cannot be undone.
          </Typography>
          {fileToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>File Name:</strong> {fileToDelete.name}</Typography>
              <Typography variant="body2"><strong>File ID:</strong> {fileToDelete.id}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content Modal */}
      <Modal
        open={isContentModalOpen}
        onClose={() => setIsContentModalOpen(false)}
        aria-labelledby="content-modal-title"
        aria-describedby="content-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: '95%',
            maxWidth: '600px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="content-modal-title" variant="h6">
              Content Details
            </Typography>
            <IconButton onClick={() => setIsContentModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box id="content-modal-description" sx={{ maxHeight: '400px', overflow: 'auto' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {JSON.stringify(selectedContent, null, 2)}
            </pre>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => setIsContentModalOpen(false)}>
              OK
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Common Toast Component */}
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </>
  );
};

export default ReferenceFileList;